import { IArticleCardItem, IArticleCardNumber } from '@lib/shared/article-card/ui';
import { OciArticle } from '../types/IOciSearchResponse';

/**
 * Bildet einen OCI-Treffer auf das Eingabemodell der gemeinsamen Artikel-Card ab
 * (EPROC-11892).
 *
 * OCI-Artikel kommen live vom Anbieter und sind duenner besetzt als indexierte Artikel: es
 * gibt keinen Preis je Inhaltseinheit, keine Staffelpreise und keine Detailansicht. Was fehlt,
 * bleibt `null` und wird von der Card ausgelassen, nicht ersetzt (BR2 in UC_SEARCH_1003).
 */
export function toArticleCardItem(article: OciArticle, catalogId: string, catalogName: string): IArticleCardItem {
    return {
        id: articleIdOf(article),
        sourceKey: `oci:${catalogId}`,
        type: article.type,
        name: article.description?.name ?? '',
        // Der Katalogname allein wuerde nicht verraten, dass der Artikel live beim Anbieter
        // gesucht wurde - dafuer steht der Zusatz.
        sourceLabel: catalogName ? `${catalogName} · OCI` : 'OCI',
        imageUrl: article.attachment || null,
        productNumber: null,
        manufacturerName: article.manufactCode || null,
        articleNumbers: articleNumbersOf(article),
        price: article.price ? { amount: article.price.price, currency: article.price.currencyIso4217 } : null,
        priceUnit: article.unit?.orderUnit || null,
        contentPrice: null,
        contentPriceUnit: null,
        // Frueher stand hier fest "1", obwohl der Anbieter eine Mindestmenge mitliefern kann.
        // Ohne Angabe laesst die Card die Zeile weg, statt eine Menge zu behaupten.
        minimumOrderQuantity: article.price?.quantityMin ?? null,
        orderQuantityInterval: null,
        deliveryTimeInDays: article.leadTime ?? null,
        hasScaledPrice: false,
    };
}

/**
 * OCI-Artikel tragen je Anbieter unterschiedliche Kennungen. Die Reihenfolge entspricht der
 * bisherigen Auswahl im Suchergebnis, damit der Warenkorb denselben Artikel wiederfindet.
 */
function articleIdOf(article: OciArticle): string {
    if (article.extProductId) {
        return article.extProductId;
    }
    if (article.vendorMat !== null && article.vendorMat !== undefined) {
        return String(article.vendorMat);
    }
    return article.matnr !== null && article.matnr !== undefined ? String(article.matnr) : '';
}

function articleNumbersOf(article: OciArticle): readonly IArticleCardNumber[] {
    return [
        { label: 'Art.', value: article.extProductId },
        { label: 'Hersteller-Nr.', value: article.manufactMat },
        { label: 'Lief.', value: article.vendorMat?.toString() },
    ].filter((number): number is IArticleCardNumber => !!number.value);
}
